import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CloneTeamFormData } from '../../_models/cloneTeamFormData';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { TeamsService } from '../../_services/teams.service';
import { AuthService } from '../../_services/auth.service';
import { SharedService } from '../../_services/shared.service';
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { ValidatorService } from '../../_services/validator.service';
import { CommonModule } from '@angular/common';
import { GraphService } from '../../_services/graph.service';
import { BaseMetadata } from '../../_models/baseMetadata';
import { CloneTeamReqDto } from '../../_dtos/cloneTeamReqDto';
import { DepartmentResDto } from '../../_dtos/departmentResDto';
import { CategoryResDto } from '../../_dtos/categoryResDto';
import { DepartmentsService } from '../../_services/departments.service';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { TranslatedToastService } from '../../_services/translated-toast.service';
import { UserResDto } from '../../_dtos/userResDto';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { CategoriesService } from '../../_services/categories.service';

@Component({
  selector: 'app-order-form-clone-teams',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatButton,
    MatError,
    CommonModule,
    MatTooltip,
    TranslateModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
  ],
  templateUrl: './order-form-clone-teams.component.html',
  standalone: true,
  styleUrl: './order-form-clone-teams.component.css',
})
export class OrderFormCloneTeamsComponent implements OnInit, OnDestroy {
  private displayName!: string;
  private members: string[] = [];
  private owners: string[] = [];
  private destroy$ = new Subject<void>();

  public department!: DepartmentResDto;
  public departments!: DepartmentResDto[];
  public categories!: CategoryResDto[];
  public groups: { displayName: string; id: string }[] = [];
  public cloneTeamFormData!: FormGroup<CloneTeamFormData>;
  public category!: string;
  public departmentShortName!: string;
  public isTabsDisabled: boolean = true;
  secondaryOwnerControl = new FormControl<UserResDto | string>('');
  teamsToCloneControl = new FormControl('');
  filteredPeople?: Observable<UserResDto[]>;
  filteredTeams!: Observable<any[]>;
  selectedTeam: any;

  constructor(
    private teamService: TeamsService,
    private authService: AuthService,
    private toastr: TranslatedToastService,
    private sharedService: SharedService,
    private validatorService: ValidatorService,
    private graphService: GraphService,
    private departmentsService: DepartmentsService,
    private categoriesService: CategoriesService,
  ) {}

  ngOnInit() {
    this.getDataFromSharedService();
    this.getGraphServiceData();
    this.displayName = this.authService.getDisplayName();
    this.initializeForm();
    this.setupFormListeners();
    this.prefillForm();
    this.setupSecondaryOwnerAutocomplete();
    this.setupTeamsToCloneAutocomplete();
  }

  displayUserFn(user: UserResDto | null): string {
    return user ? `${user.displayName}` : '';
  }

  async onSubmit() {
    if (this.cloneTeamFormData.valid) {
      const formValues = this.cloneTeamFormData.getRawValue();
      const teamMetadata: BaseMetadata = {
        visibility: formValues.visibility,
        department: this.departmentShortName,
      };

      const username = this.authService.getUsername();
      const cloneTeamReqDto: CloneTeamReqDto = {
        teamsToClone: this.selectedTeam.id,
        elementsToClone: formValues.elementsToClone,
        members: this.members,
        owners: this.owners,
        name: formValues.name,
        mailNickname: formValues.mailNickname,
        category: formValues.category,
        primaryOwner: username,
        secondaryOwner: formValues.secondaryOwner?.userPrincipalName!,
        createdBy: username,
        metadata: teamMetadata,
      };

      if (formValues.elementsToClone.includes('Members')) {
        this.graphService.getMembers(formValues.teamsToClone).subscribe({
          next: (data) => {
            if (data.value)
              data.value.forEach((member: { id: string }) =>
                this.members.push(member.id),
              );
          },
        });

        this.graphService.getOwners(formValues.teamsToClone).subscribe({
          next: (data) => {
            if (data.value) {
              data.value.forEach((owner: { id: string }) =>
                this.owners.push(owner.id),
              );
            }
          },
        });
      }

      this.sharedService.closeModal();
      await this.toastr.show(
        'success',
        'TOASTS.TEAMS.CLONE_SUBMIT_MESSAGE',
        'TOASTS.TEAMS.CLONE_SUBMIT_TITLE',
      );

      this.teamService.cloneTeam(cloneTeamReqDto).subscribe({
        next: async () => {
          await this.toastr.show(
            'success',
            'TOASTS.TEAMS.CLONE_SUCCESS',
            undefined,
            { teamName: cloneTeamReqDto.name },
          );
        },
      });
    }
  }

  get name() {
    return this.cloneTeamFormData.get('name');
  }

  get mailNickname() {
    return this.cloneTeamFormData.get('mailNickname');
  }

  get errorMessage() {
    if (this.name?.hasError('required')) return 'Name is required';
    else if (this.name?.hasError('hasForbiddenChars'))
      return 'Contains forbidden Character';
    else if (this.name?.hasError('exceedsMaxLength'))
      return 'The provided name is too long';
    else if (this.name?.hasError('mailNicknameUnavailable'))
      return 'This name is already taken';
    else return '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getDataFromSharedService() {
    this.departments = this.departmentsService.getDepartments();
    this.categories = this.categoriesService.getSpCategories();
    this.department = this.departmentsService.getDepartment();
    const userType = this.graphService.getUserType();
    this.category = userType ? userType.toLowerCase() : 'NO CATEGORY FOUND!';
  }

  private getGraphServiceData() {
    this.graphService.getOwnedObjects().subscribe({
      next: (data) => {
        if (Array.isArray(data) && data.length > 0) {
          data.forEach(
            (group: {
              resourceProvisioningOptions: string[];
              displayName: string;
              id: string;
            }) => {
              if (group.resourceProvisioningOptions?.includes('Team')) {
                this.groups.push({
                  displayName: group.displayName,
                  id: group.id,
                });
              }
            },
          );
          this.groups = this.groups.sort((a, b) =>
            a.displayName.localeCompare(b.displayName),
          );
        }
      },
      error: (error) => {
        console.error('Error fetching groups', error);
      },
    });
  }

  private initializeForm() {
    this.cloneTeamFormData = new FormGroup<CloneTeamFormData>({
      teamsToClone: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
        asyncValidators: [
          this.validatorService.validateMailNickname(this.department.shortName),
        ],
        updateOn: 'blur',
      }),
      elementsToClone: new FormControl([''], {
        nonNullable: true,
        validators: [Validators.required],
      }),
      mailNickname: new FormControl(
        { value: '', disabled: true },
        { nonNullable: true },
      ),
      category: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      primaryOwner: new FormControl(
        { value: '', disabled: true },
        { nonNullable: true },
      ),
      secondaryOwner: new FormControl<UserResDto | null>(null, {
        nonNullable: false,
        validators: [this.validatorService.userObjectValidator()],
      }),
      visibility: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      department: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });

    this.secondaryOwnerControl = this.cloneTeamFormData.get(
      'secondaryOwner',
    ) as FormControl;
    this.teamsToCloneControl = this.cloneTeamFormData.get(
      'teamsToClone',
    ) as FormControl;
  }

  onSecondaryOwnerSelected(event: MatAutocompleteSelectedEvent) {
    const selectedUser = event.option.value as UserResDto;
    this.secondaryOwnerControl.setValue(selectedUser);
  }

  onTeamToCloneSelected(event: MatAutocompleteSelectedEvent) {
    const selected = event.option.value;
    console.log(selected);
    this.selectedTeam = selected;

    this.cloneTeamFormData.patchValue({
      teamsToClone: selected.displayName,
    });
  }

  private setupFormListeners() {
    this.cloneTeamFormData.get('name')?.valueChanges.subscribe((value) => {
      const revisedValue = this.validatorService.reviseValue(value);
      const fullMailNickname = this.cloneTeamFormData.get('mailNickname')!;

      if (!fullMailNickname.value) {
        const newValue = `${this.departmentShortName}-${revisedValue}`;
        this.cloneTeamFormData
          .get('mailNickname')
          ?.setValue(newValue, { emitEvent: false });
      } else {
        const parts = fullMailNickname.value.split('-');
        if (parts.length > 0) {
          const department = parts[0];
          const newValue = `${department}-${revisedValue}`;
          this.cloneTeamFormData
            .get('mailNickname')
            ?.setValue(newValue, { emitEvent: false });
        }
      }
    });

    this.cloneTeamFormData
      .get('department')!
      .valueChanges.subscribe((departmentName) => {
        this.departmentsService
          .getDepartmentObservable(departmentName)
          .subscribe({
            next: (data) => {
              this.departmentShortName = data.shortName;

              const fullMailNickname =
                this.cloneTeamFormData.get('mailNickname')!;
              const parts = fullMailNickname.value.split('-');
              if (parts.length > 1) {
                const mailNickname = parts.slice(1).join('-');
                const revisedValue = `${this.departmentShortName}-${mailNickname}`;
                this.cloneTeamFormData
                  .get('mailNickname')
                  ?.setValue(revisedValue, { emitEvent: false });
              }
            },
          });
      });

    this.cloneTeamFormData
      .get('elementsToClone')
      ?.valueChanges.subscribe((selectedElements) => {
        this.isTabsDisabled =
          !selectedElements || !selectedElements.includes('channels');

        if (
          this.isTabsDisabled &&
          selectedElements &&
          selectedElements.includes('tabs')
        ) {
          const updatedElements = selectedElements.filter(
            (element: string) => element !== 'tabs',
          );
          this.cloneTeamFormData
            .get('elementsToClone')
            ?.setValue(updatedElements, { emitEvent: false });
        }
      });
  }

  private prefillForm() {
    this.cloneTeamFormData.patchValue({
      primaryOwner: this.displayName,
      department: this.department.name,
      category: this.category,
    });
  }

  private setupSecondaryOwnerAutocomplete() {
    this.filteredPeople = this.secondaryOwnerControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchValue) => {
        if (typeof searchValue !== 'string' || !searchValue.trim())
          return of([]);

        const searchTerm = searchValue.trim();
        return this.graphService.searchPeople(searchTerm);
      }),
    );
  }

  private setupTeamsToCloneAutocomplete(): void {
    this.filteredTeams = this.teamsToCloneControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value) => {
        if (!value || typeof value !== 'string' || value.trim() === '') {
          return of([]);
        }
        return of(this.searchOwnedObjects(value.trim()));
      }),
    );
  }

  private searchOwnedObjects(value: string): any[] {
    const sanitized = value.toLowerCase();
    return this.groups.filter((obj: any) =>
      obj.displayName?.toLowerCase().includes(sanitized),
    );
  }
}
